import React, { useState, useEffect, useLayoutEffect, useRef } from 'react'
import { Card, OverlayTrigger, Tooltip } from 'react-bootstrap'
import OrderLogisticsCard from './OrderLogisticsCard'
import { EditSvg } from '../../svgFiles/EditSvg'
import { UploadSvg } from '../../svgFiles/UploadSvg'
import { DownloadSvg } from '../../svgFiles/DownloadSvg'
import { isVaChannel, resolveVaProviderName } from '../../helpers/vaInvoiceUtils'
import { openStoredFileUrl } from '../../helpers/vaParseStorage'
import {
    useUpdateOriginalPoMutation,
    useUpdateInvoiceFileMutation,
} from '../../app/orderApi/OrderApi'
import { showToast } from '../../helpers/Utils'
import CopyToClipboard from '../../components/shared/copyToClipboard/CopyToClipboard'
import AddOriginalPoNumberModal from '../../components/shared/modalContent/AddOriginalPoNumberModal'
import EditProviderNameModal from '../../components/shared/modalContent/EditProviderNameModal'
import UploadVaDocumentModal from '../../components/shared/modalContent/UploadVaDocumentModal'
import { formatDashboardDateSlash } from '../../helpers/dashboardTime'

function formatRedemptionExpiry(value) {
    return formatDashboardDateSlash(value, '—');
}

function resolveInvoiceFileUrl(order) {
    return (
        order?.invoiceFile ||
        order?.invoiceFileKey ||
        order?.aiParseMeta?.invoiceFileKey ||
        ""
    );
}

/** Prefer stored upload name; else last path segment (strip timestamp_ prefix). */
function resolveStoredFileName(storedName, fileUrl, fallbackLabel) {
    const named = String(storedName || "").trim();
    if (named) return named;
    const raw = String(fileUrl || "").trim();
    if (!raw) return fallbackLabel;
    try {
        const pathOnly = raw.split("?")[0].split("#")[0];
        const segment = decodeURIComponent(
            pathOnly.split("/").filter(Boolean).pop() || ""
        );
        if (!segment) return fallbackLabel;
        return segment.replace(/^\d+_/, "") || fallbackLabel;
    } catch {
        return fallbackLabel;
    }
}

function resolveActivationCodes(order) {
    const raw = order?.redemptionCodes ?? order?.redemptionCode;
    if (Array.isArray(raw)) {
        return raw.map((code) => String(code || "").trim()).filter(Boolean);
    }
    const text = String(raw || "").trim();
    if (!text) return [];
    return text
        .split(/[\n,]+/)
        .map((code) => code.trim())
        .filter(Boolean);
}

/** File | name/action pill used on VA order meta card. */
function VaFileChip({ value, icon, onClick, tooltipId, ariaLabel }) {
    const label = String(value || "").trim();
    const chip = (
        <button
            type="button"
            className="va-file-chip"
            onClick={onClick}
            aria-label={ariaLabel || label || "File"}
        >
            <span className="va-file-chip__prefix">File</span>
            <span className="va-file-chip__divider" aria-hidden="true" />
            <span className="va-file-chip__value">{label}</span>
            {icon ? <span className="va-file-chip__icon">{icon}</span> : null}
        </button>
    );

    if (!label || !tooltipId) return chip;

    return (
        <OverlayTrigger
            placement="top"
            delay={{ show: 150, hide: 50 }}
            overlay={
                <Tooltip id={tooltipId} className="va-doc-file-tooltip">
                    {label}
                </Tooltip>
            }
        >
            {chip}
        </OverlayTrigger>
    );
}

function VaAddChip({ onClick, ariaLabel = "Add" }) {
    return (
        <button
            type="button"
            className="va-add-chip"
            onClick={onClick}
            aria-label={ariaLabel}
        >
            Add <span aria-hidden="true">+</span>
        </button>
    );
}

const UserInfo = ({
    currentOrder,
    setShowAddnoteModal,
    setShowEditCustomerModal,
    canEditNotes = true,
    canEditCustomer = true,
    canEditOriginalPo = false,
    onOriginalPoUpdated,
    onProviderNameSave,
    providerNameSaving = false,
    hideCustomerDetails = false,
    hideCopyStatusLink = false,
    hideRedemptionCode = false,
}) => {
    const [showFullNote, setShowFullNote] = useState(false);
    const [noteOverflows, setNoteOverflows] = useState(false);
    const noteTextRef = useRef(null);
    const [showOriginalPoNumberModal, setShowOriginalPoNumberModal] =
        useState(false);
    const [showOriginalPoFileModal, setShowOriginalPoFileModal] =
        useState(false);
    const [showInvoiceFileModal, setShowInvoiceFileModal] = useState(false);
    const [showEditProviderNameModal, setShowEditProviderNameModal] =
        useState(false);
    const [updateOriginalPo, { isLoading: isSavingOriginalPo }] =
        useUpdateOriginalPoMutation();
    const [updateInvoiceFile, { isLoading: isSavingInvoiceFile }] =
        useUpdateInvoiceFileMutation();

    const existingPoNumber = String(
        currentOrder?.invoicePoNumber || currentOrder?.originalPoNumber || ""
    ).trim();
    const existingOriginalPoFile = String(
        currentOrder?.originalPoFile || currentOrder?.originalPoFileKey || ""
    ).trim();
    const originalPoFileDisplayName = resolveStoredFileName(
        currentOrder?.originalPoFileName,
        existingOriginalPoFile,
        "Original VA PO"
    );
    const canSetPoNumber = Boolean(canEditOriginalPo);
    const canUploadOriginalPoFile = Boolean(canEditOriginalPo);

    useEffect(() => {
        setShowOriginalPoNumberModal(false);
        setShowOriginalPoFileModal(false);
        setShowInvoiceFileModal(false);
    }, [currentOrder?.ItemID]);

    const NoteUpdates = (currentOrder?.ChangeLog || []).filter(({ field }) => field === "Note");
    const providerNameUpdates = (currentOrder?.ChangeLog || []).filter(
        ({ field }) =>
            field === "providerName" ||
            field === "vaAudiologistName" ||
            field === "provider"
    );
    const orderFromUpdates = (currentOrder?.ChangeLog || []).filter(
        ({ field }) => field === "OrderFrom" || field === "orderFrom"
    );
    const customerEmailUpdates = (currentOrder?.ChangeLog || []).filter(({ field }) => field === "customerEmail");
    const customerNameUpdates = (currentOrder?.ChangeLog || []).filter(({ field }) => field === "customerName");
    const addressUpdates = (currentOrder?.ChangeLog || []).filter(({ field }) => field === "Address1");
    const shippingUpdates = (currentOrder?.ChangeLog || []).filter(({ field }) => field === "ShippingName");
    const phoneUpdates = (currentOrder?.ChangeLog || []).filter(({ field }) => field === "ShippingPhone");

    const note = currentOrder?.Note || "";

    const orderFrom =
        currentOrder?.OrderFrom ?? currentOrder?.orderFrom ?? "";
    const facilityName = String(currentOrder?.vaHospitalName || "").trim();

    const distributor =
        currentOrder?.distributor && typeof currentOrder.distributor === "object"
            ? currentOrder.distributor
            : null;
    const distributorName = [
        currentOrder?.DistributorName,
        currentOrder?.distributorName,
        currentOrder?.AssignedDistributor,
        currentOrder?.assignedDistributor,
        currentOrder?.AssignedDistributorName,
        currentOrder?.assignedDistributorName,
        currentOrder?.VaDistributorName,
        currentOrder?.vaDistributorName,
        distributor?.name,
        distributor?.DistributorName,
    ]
        .map((value) => String(value || "").trim())
        .find(Boolean) || "";
    const distributorTerritory = [
        currentOrder?.DistributorTerritory,
        currentOrder?.distributorTerritory,
        distributor?.territory,
        Array.isArray(distributor?.territories)
            ? distributor.territories.filter(Boolean).join(", ")
            : "",
        Array.isArray(currentOrder?.DistributorTerritories)
            ? currentOrder.DistributorTerritories.filter(Boolean).join(", ")
            : "",
    ]
        .map((value) => String(value || "").trim())
        .find(Boolean) || "";
    const assignedDistributorLabel = [distributorName, distributorTerritory]
        .filter(Boolean)
        .join(" - ");
    const activationCodes = resolveActivationCodes(currentOrder);
    const orderFromDisplay =
        facilityName ||
        orderFrom ||
        assignedDistributorLabel ||
        "";

    const hasShippingAddress = Boolean(
        currentOrder?.Address1 ||
        currentOrder?.Address2 ||
        currentOrder?.ShippingName ||
        currentOrder?.ShippingCity ||
        currentOrder?.ShippingCountry ||
        currentOrder?.vaShipToRaw
    );

    const isVaOrder = isVaChannel(currentOrder);
    const providerNameDisplay = resolveVaProviderName(currentOrder);
    const invoiceFileUrl = resolveInvoiceFileUrl(currentOrder);
    const invoiceFileDisplayName = resolveStoredFileName(
        currentOrder?.invoiceFileName ||
            currentOrder?.aiParseMeta?.invoiceFileName,
        invoiceFileUrl,
        "Lovell PO File"
    );
    const canUploadInvoiceFile = Boolean(canEditOriginalPo);

    useEffect(() => {
        setShowFullNote(false);
    }, [note]);

    useLayoutEffect(() => {
        if (showFullNote) return;
        const el = noteTextRef.current;
        if (!el) {
            setNoteOverflows(false);
            return;
        }
        setNoteOverflows(el.scrollHeight > el.clientHeight + 1);
    }, [note, showFullNote]);
    const editIconStyle = {
        cursor: "pointer",
        opacity: 1,
        pointerEvents: "auto",
        display: "inline-flex",
        alignItems: "center",
    };

    return (
        <>
            {!hideCustomerDetails && (
            <Card className="p-3 user-info-card">
                <div className="d-flex align-items-center justify-content-end mb-1">
                    {canEditCustomer && (
                        <span
                            style={editIconStyle}
                            onClick={() => setShowEditCustomerModal?.(true)}
                            aria-label="Edit customer information"
                        >
                            <EditSvg />
                        </span>
                    )}
                </div>

                <h6 className="card-label d-flex align-items-center gap-1">
                    {isVaOrder ? "Patient name" : "Customer"}
                    {((customerNameUpdates?.length > 0 && customerNameUpdates.some(u => u.isFieldSeen === false))) && (
                    <div className="green-dot"></div>
                )}</h6>
                <p className="card-text">
                    {isVaOrder
                        ? currentOrder?.patientName || currentOrder?.customerName
                        : currentOrder?.customerName}
                </p>

                <h6 className="card-label d-flex align-items-center gap-1">Contact Information
                    {((customerEmailUpdates?.length > 0 && customerEmailUpdates.some(u => u.isFieldSeen === false))) && (
                        <div className="green-dot"></div>
                    )}
                </h6>
                <p className="card-text">
                    {currentOrder?.customerEmail}
                </p>

                {hasShippingAddress && (
                <>
                <h6 className="card-label d-flex align-items-center gap-1">Shipping Address
                    {((addressUpdates?.length > 0 && addressUpdates.some(u => u.isFieldSeen === false)) ||
                        (shippingUpdates?.length > 0 && shippingUpdates.some(u => u.isFieldSeen === false))) && (
                            <div className="green-dot"></div>
                        )}</h6>
                <p className="card-text">
                    {(isVaOrder
                        ? [
                            // Same field order as Create Order / Edit Customer — exact saved values.
                            currentOrder?.vaClinicName || currentOrder?.clinicName,
                            (() => {
                              const rawLines = String(currentOrder?.vaShipToRaw || "")
                                .replace(/\r\n/g, "\n")
                                .split("\n")
                                .map((line) => line.trim())
                                .filter(Boolean);
                              return rawLines.find((line) =>
                                /^(ATTN\s*PO:|P\.?\s*O\.?\s*#?)/i.test(line)
                              ) || "";
                            })(),
                            currentOrder?.ShippingName,
                            currentOrder?.Address1,
                            currentOrder?.Address2,
                            currentOrder?.ShippingCity,
                            currentOrder?.ShippingProvinceCode ||
                                currentOrder?.ShippingProvince,
                            currentOrder?.ShippingZip,
                            currentOrder?.ShippingCountry,
                          ].filter(Boolean)
                        : [
                            currentOrder?.ShippingName,
                            currentOrder?.Address1,
                            currentOrder?.Address2,
                            currentOrder?.ShippingStreet,
                            currentOrder?.ShippingCity,
                            currentOrder?.ShippingProvinceCode ||
                                currentOrder?.ShippingProvince,
                            currentOrder?.ShippingZip,
                            currentOrder?.ShippingCountry || currentOrder?.AddressCountryCode,
                          ].filter(Boolean)
                    ).map((line, index) => (
                        <div key={index}>{line}</div>
                    ))}
                    {currentOrder?.shippingEmail ? (
                        <div>{currentOrder.shippingEmail}</div>
                    ) : null}
                </p>

                <h6 className="card-label d-flex align-items-center gap-1">Phone  {(
                    (phoneUpdates?.length > 0 && phoneUpdates.some(u => u.isFieldSeen === false))) && (
                        <div className="green-dot"></div>
                    )}</h6>
                <p className="card-text mb-0">
                    {currentOrder?.ShippingPhone}
                </p>
                </>
                )}
            </Card>
            )}

            {!hideCustomerDetails && isVaOrder && (
                <Card className="p-3 mt-3 user-info-card">
                    <div className="d-flex align-items-center justify-content-between mb-1">
                        <h6 className="card-label d-flex align-items-center gap-1 mb-0">
                            Provider name
                            {(
                                providerNameUpdates?.length > 0 &&
                                providerNameUpdates.some((u) => u.isFieldSeen === false)
                            ) && <div className="green-dot" />}
                        </h6>
                        {canEditCustomer && (
                            <span
                                style={editIconStyle}
                                onClick={() => setShowEditProviderNameModal(true)}
                                aria-label="Edit Provider name"
                            >
                                <EditSvg />
                            </span>
                        )}
                    </div>
                    <p className="card-text mb-0">
                        {providerNameDisplay || "—"}
                    </p>
                    <EditProviderNameModal
                        show={showEditProviderNameModal}
                        setShow={setShowEditProviderNameModal}
                        initialValue={providerNameDisplay}
                        onSave={onProviderNameSave}
                        confirmLoading={providerNameSaving}
                    />
                </Card>
            )}

            {hideCustomerDetails && (
                <Card className="p-3">
                    <h6 className="card-label">Expired Redemption</h6>
                    <p className="card-text mb-0">
                        {formatRedemptionExpiry(currentOrder?.RedemptionExpiresAt)}
                    </p>
                </Card>
            )}

            {isVaOrder && (
            <>
            <AddOriginalPoNumberModal
                show={showOriginalPoNumberModal}
                setShow={setShowOriginalPoNumberModal}
                initialValue={existingPoNumber}
                confirmLoading={isSavingOriginalPo}
                onSave={async (invoicePoNumber) => {
                    try {
                        await updateOriginalPo({
                            ItemID: currentOrder.ItemID,
                            invoicePoNumber,
                        }).unwrap();
                        showToast("PO Number saved", "success");
                        setShowOriginalPoNumberModal(false);
                        onOriginalPoUpdated?.();
                    } catch (error) {
                        showToast(
                            error?.data?.message ||
                                "Unable to save PO Number.",
                            "error"
                        );
                    }
                }}
            />
            <UploadVaDocumentModal
                show={showOriginalPoFileModal}
                setShow={setShowOriginalPoFileModal}
                title={
                    existingOriginalPoFile
                        ? "Replace Original VA PO"
                        : "Upload Original VA PO"
                }
                confirmLoading={isSavingOriginalPo}
                onSave={async (file) => {
                    try {
                        await updateOriginalPo({
                            ItemID: currentOrder.ItemID,
                            originalPoFile: file,
                        }).unwrap();
                        showToast("Original VA PO uploaded", "success");
                        setShowOriginalPoFileModal(false);
                        onOriginalPoUpdated?.();
                    } catch (error) {
                        showToast(
                            error?.data?.message ||
                                "Unable to upload Original VA PO.",
                            "error"
                        );
                    }
                }}
            />
            <UploadVaDocumentModal
                show={showInvoiceFileModal}
                setShow={setShowInvoiceFileModal}
                title={
                    invoiceFileUrl
                        ? "Replace Lovell PO File"
                        : "Upload Lovell PO File"
                }
                confirmLoading={isSavingInvoiceFile}
                onSave={async (file) => {
                    try {
                        await updateInvoiceFile({
                            ItemID: currentOrder.ItemID,
                            invoiceFile: file,
                        }).unwrap();
                        showToast("Lovell PO File uploaded", "success");
                        setShowInvoiceFileModal(false);
                        onOriginalPoUpdated?.();
                    } catch (error) {
                        showToast(
                            error?.data?.message ||
                                "Unable to upload Lovell PO File.",
                            "error"
                        );
                    }
                }}
            />
            <Card className="p-3 mt-3 va-order-meta-card">
                <div className="va-order-meta-field">
                    <h6 className="card-label d-flex align-items-center gap-2 mb-1">
                        Order from
                        {(
                            orderFromUpdates?.length > 0 &&
                            orderFromUpdates.some((u) => u.isFieldSeen === false)
                        ) && <div className="green-dot" />}
                    </h6>
                    <p className="card-text mb-0">
                        {orderFromDisplay || "No assigned distributor"}
                    </p>
                </div>

                <div className="va-order-meta-field">
                    <h6 className="card-label mb-2">Lovell PO File</h6>
                    {invoiceFileUrl ? (
                        <VaFileChip
                            value={invoiceFileDisplayName}
                            tooltipId={`invoice-file-${currentOrder?.ItemID || "file"}`}
                            icon={<DownloadSvg />}
                            ariaLabel="Download Lovell PO File"
                            onClick={() => {
                                window.open(
                                    invoiceFileUrl,
                                    "_blank",
                                    "noopener,noreferrer"
                                );
                            }}
                        />
                    ) : canUploadInvoiceFile ? (
                        <VaFileChip
                            value="Upload"
                            icon={<UploadSvg />}
                            ariaLabel="Upload Lovell PO File"
                            onClick={() => setShowInvoiceFileModal(true)}
                        />
                    ) : (
                        <p className="card-text mb-0 text-muted">
                            No Lovell PO File available for this order
                        </p>
                    )}
                </div>

                <div className="va-order-meta-field">
                    <h6 className="card-label mb-2">PO Number</h6>
                    {existingPoNumber ? (
                        <div className="va-po-number-row">
                            <div className="va-po-number-pill">
                                <span className="va-po-number-pill__value">
                                    {existingPoNumber}
                                </span>
                                <CopyToClipboard
                                    value={existingPoNumber}
                                    className="va-po-number-pill__copy"
                                />
                            </div>
                            {canSetPoNumber ? (
                                <button
                                    type="button"
                                    className="va-meta-edit-btn"
                                    onClick={() => setShowOriginalPoNumberModal(true)}
                                    aria-label="Edit PO Number"
                                >
                                    <EditSvg />
                                </button>
                            ) : null}
                        </div>
                    ) : canSetPoNumber ? (
                        <VaAddChip
                            onClick={() => setShowOriginalPoNumberModal(true)}
                            ariaLabel="Add PO Number"
                        />
                    ) : (
                        <p className="card-text mb-0 text-muted">
                            No PO Number for this order
                        </p>
                    )}
                </div>

                <div className="va-order-meta-field">
                    <h6 className="card-label mb-2">Original VA PO</h6>
                    {existingOriginalPoFile ? (
                        <div className="va-doc-file-row">
                            <div className="va-doc-file-row__chip">
                                <VaFileChip
                                    value={originalPoFileDisplayName}
                                    tooltipId={`original-po-file-${currentOrder?.ItemID || "file"}`}
                                    icon={<DownloadSvg />}
                                    ariaLabel="Download Original VA PO"
                                    onClick={() => {
                                        openStoredFileUrl(
                                            currentOrder.originalPoFile ||
                                                currentOrder.originalPoFileKey
                                        );
                                    }}
                                />
                            </div>
                            {canUploadOriginalPoFile ? (
                                <button
                                    type="button"
                                    className="va-meta-edit-btn"
                                    onClick={() => setShowOriginalPoFileModal(true)}
                                    aria-label="Replace Original VA PO"
                                >
                                    <EditSvg />
                                </button>
                            ) : null}
                        </div>
                    ) : canUploadOriginalPoFile ? (
                        <VaFileChip
                            value="Upload"
                            icon={<UploadSvg />}
                            ariaLabel="Upload Original VA PO"
                            onClick={() => setShowOriginalPoFileModal(true)}
                        />
                    ) : (
                        <p className="card-text mb-0 text-muted">
                            No Original VA PO available for this order
                        </p>
                    )}
                </div>

            </Card>
            </>
            )}

            {/* Shopify / legacy: show activation codes (looked up client-side). VA/demo stay hidden. */}
            {!isVaOrder && !hideRedemptionCode && activationCodes.length > 0 ? (
                <Card className="p-3 mt-3 va-order-meta-card">
                    <div className="va-order-meta-field va-order-meta-field--last">
                        <h6 className="card-label mb-2">Activation code</h6>
                        <div className="va-activation-code-list">
                            {activationCodes.map((code) => (
                                <div
                                    key={code}
                                    className="va-activation-code-pill"
                                >
                                    <span className="va-activation-code-pill__value">
                                        {code}
                                    </span>
                                    <CopyToClipboard
                                        value={code}
                                        className="va-activation-code-pill__copy"
                                    />
                                </div>
                            ))}
                        </div>
                    </div>
                </Card>
            ) : null}

            <OrderLogisticsCard currentOrder={currentOrder} />

            <Card className="p-3 mt-3">
                <h6 className="d-flex align-items-center gap-3">
                    Internal notes{((NoteUpdates?.length > 0 && NoteUpdates.some(u => u.isFieldSeen === false))) && (
                        <div className="green-dot"></div>
                    )}
                    {canEditNotes && (
                    <span className="ms-auto" style={editIconStyle}
                        onClick={() => setShowAddnoteModal(true)}
                    >
                        <EditSvg />
                    </span>
                    )}

                </h6>

                <div
                    ref={noteTextRef}
                    className={`card-text internal-notes-text${showFullNote ? "" : " internal-notes-text--clamp"}`}
                >
                    {note || "No notes"}
                </div>

                {note && (showFullNote || noteOverflows) && (
                    <span
                        role="button"
                        onClick={() => setShowFullNote((open) => !open)}
                        style={{ color: "blue", cursor: "pointer" }}
                    >
                        {showFullNote ? "Show less" : "Read more"}
                    </span>
                )}
            </Card>
        </>
    )
}

export default UserInfo
